import type { FC, PropsWithChildren } from 'react';

import { createContext, useContext, useEffect, useState } from 'react';

import { useUserContext } from './UserContext';
import { useCookieContext } from '~/utilities/context/dynamic/CookieContext';
import type { Configuration } from '~/utilities/graphql/codegen';

interface ConsentContextType {
  hasAnalyticsConsent: boolean;
  hasMarketingConsent: boolean;
  /**
   * Has both marketing and analytics consent
   */
  hasFullConsent: boolean;
  /**
   * Has either analytics or marketing consent. `undefined` while the user hasn't made
   * an explicit choice yet (no OneTrust/coco cookie recorded) - distinct from `false`,
   * which means consent was explicitly rejected.
   */
  hasAnyConsent: boolean | undefined;
  shouldReportAnalytics: boolean;
}

const defaultValue: ConsentContextType = {
  hasAnalyticsConsent: false,
  hasMarketingConsent: false,
  hasFullConsent: false,
  hasAnyConsent: undefined,
  shouldReportAnalytics: false,
};

const ConsentContext = createContext<ConsentContextType>(defaultValue);

export const useConsentContext = () => useContext(ConsentContext);

export const ConsentProvider: FC<PropsWithChildren<{ configuration: Configuration }>> = ({
  children,
  configuration,
}) => {
  const [consent, setConsent] = useState<ConsentContextType>(defaultValue);
  const { commonData } = useUserContext();
  const { cocoCookie, oneTrustCookie, optanonAlertBoxClosedCookie, imanrut, rsu } =
    useCookieContext();
  const { enableCMP, cmpDomainId, enableCookieWall } = configuration;

  useEffect(() => {
    let marketingConsent = false;
    let analyticsConsent = false;
    let isConsentPending = false;

    if (enableCMP) {
      if (!cmpDomainId) {
        // eslint-disable-next-line no-console
        console.error('CMP domain ID is missing');
      }

      // OneTrust writes `oneTrustCookie` eagerly, with default (rejected) groups, before the
      // banner has even been closed - it's not a "decision made" signal. `optanonAlertBoxClosedCookie`
      // is only set once the user actually closes the banner (accept/reject/save preferences).
      if (!optanonAlertBoxClosedCookie) {
        isConsentPending = true;
      } else if (oneTrustCookie) {
        analyticsConsent = !!/C0002:1/.test(oneTrustCookie.toString());
        marketingConsent = !!/C0004:1/.test(oneTrustCookie.toString());
      }
    } else if (!enableCookieWall) {
      analyticsConsent = true;
      marketingConsent = true;
    } else if (cocoCookie) {
      const coco = cocoCookie.toString();

      analyticsConsent = coco.at(2)?.includes('1') ?? false;
      marketingConsent = coco.startsWith('11');
    } else {
      isConsentPending = true;
    }

    const fullConsent = analyticsConsent && marketingConsent;
    const hasAnyConsent = isConsentPending ? undefined : analyticsConsent || marketingConsent;
    const shouldReportAnalyticsState =
      !!rsu && !imanrut && !commonData?.internal && analyticsConsent;

    setConsent({
      hasAnalyticsConsent: analyticsConsent,
      hasMarketingConsent: marketingConsent,
      hasFullConsent: fullConsent,
      hasAnyConsent,
      shouldReportAnalytics: shouldReportAnalyticsState,
    });
  }, [
    cocoCookie,
    oneTrustCookie,
    optanonAlertBoxClosedCookie,
    enableCMP,
    cmpDomainId,
    enableCookieWall,
    rsu,
    imanrut,
    commonData?.internal,
  ]);

  return <ConsentContext.Provider value={consent}>{children}</ConsentContext.Provider>;
};
